import { jwtDecode } from 'jwt-decode';
import { SnackbarSupport } from '@scramble/ui';
import { applyMiddleware, combineReducers, compose, createStore } from 'redux';
import { thunk } from 'redux-thunk';
import { AUTH_TEMP_TOKEN_KEY } from '@/pages/AuthPage/utils';
import {
  clearDirectAccessToken,
  isImpersonationSession,
} from '@/helpers/authSession';
import { queryClient } from '@/queryClient';
import { clearLocalSession, logoutUser } from '@/store/services/userServices';
import { isRefreshRequest, refreshAccessToken } from '@/store/tokenRefresh';
import axios, { getRequestHeaderParams } from '../axios-api';
import { useUserStore } from './userStore';

// TODO Удалить когда будет редизайн этих разделов
import commonReducer from './reducers/commonReducer';
import helpReducer from './reducers/helpReducer';
import blogReducer from './reducers/blogReducer';

// TODO Удалить когда будет редизайн этих разделов
const rootReducer = combineReducers({
  commonStore: commonReducer,
  helpStore: helpReducer,
  blogStore: blogReducer,
});

const composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;

const middleware = [thunk];

const enhancers = composeEnhancers(applyMiddleware(...middleware));

const store = createStore(rootReducer, {}, enhancers);

const isImpersonationRequest = requestConfig => {
  const auth = requestConfig?.headers?.Authorization;
  if (!auth) return false;

  try {
    return !!jwtDecode(String(auth).replace(/^(Token|Bearer)\s+/i, '')).admin_initiated;
  } catch {
    return false;
  }
};

axios.interceptors.request.use(async config => {
  try {
    const headerParams = getRequestHeaderParams();

    config.headers = {
      ...config.headers,
      ...headerParams,
    };

    const token =
      useUserStore.getState().accessToken ||
      localStorage.getItem(AUTH_TEMP_TOKEN_KEY);

    if (token) {
      config.headers.Authorization = `Token ${token}`;
    }
  } catch (e) {
    console.error(e.message);
  }
  return config;
});

axios.interceptors.response.use(
  response => response,
  async error => {
    const status = error.response?.status;
    const requestID = error.config?.headers?.['X-Request-ID'];

    if (status === 500) {
      const text = `Error: ${error.response.statusText}\nError code: ${status}\nRequest ID: ${requestID}\nPlease, send this code to support.`;
      SnackbarSupport({ text });
    }

    if (
      status === 401 &&
      error.response?.data?.message === 'access_token expired'
    ) {
      const originalConfig = error.config;

      if (isImpersonationSession() || isImpersonationRequest(originalConfig)) {
        clearDirectAccessToken();
        queryClient.clear();
        useUserStore.getState().clearTokens();
        return Promise.reject(error);
      }

      // A request that still reports an expired access token after a
      // successful refresh must not start another refresh round.
      if (originalConfig._isRetriedAfterRefresh) {
        clearLocalSession();
        return Promise.reject(error);
      }

      return refreshAccessToken().then(
        () => {
          originalConfig._isRetriedAfterRefresh = true;
          // Let the request interceptor re-attach the fresh token.
          delete originalConfig.headers.Authorization;
          return axios(originalConfig);
        },
        err => {
          // A failed refresh means this client has no reachable server
          // session; POSTing /logout/ here would clear the shared refresh
          // cookie a sibling tab just rotated and kill its live session.
          clearLocalSession();
          return Promise.reject(err);
        },
      );
    }

    // Gate on a local session rather than on the URL: once the tokens are
    // gone the next request 401s without any Authorization header, and
    // POSTing /logout/ then would clear the refresh cookie shared with
    // sibling tabs and kill their live sessions.
    if (
      status === 401 &&
      useUserStore.getState().accessToken &&
      !isRefreshRequest(error.config)
    ) {
      logoutUser();
    }

    return error.response
      ? error.response
      : Promise.reject(error);
  },
);

export default store;
